<template>
	<div>
		<v-btn min-width="0" text @click="showNotifications">
			<v-badge
				:content="notifications.length"
				:value="notifications.length"
				color="red"
				overlap
				bordered
			>
				<v-icon>mdi-bell</v-icon>
			</v-badge>
		</v-btn>
		<v-dialog v-model="showDialog" max-width="800" persistent>
			<v-card class="notification-detail-card">
				<v-card-text class="notification-content py-4">
					<v-list
						class="notifications-list"
						v-if="notifications.length"
					>
						<div
							v-for="(notification, index) in notifications"
							:key="index"
							class="mb-3"
						>
							<v-card elevation="3" class="notification-card">
								<v-list-item class="notifications-list-item">
									<v-list-item-content class="ml-2">
										<v-list-item-title class="pb-1">
											<template
												v-if="
													isExportFile(notification)
												"
											>
												<span class="export-file-row">
													File
													<a
														href="javascript:void(0)"
														@click.prevent="
															exportNotificationFile(
																notification
															)
														"
														class="export-file-link"
													>
														<v-icon
															color="primary"
															size="18"
															class="mr-2"
															>mdi-file-check</v-icon
														>
														<span
															class="export-file-title"
															>{{
																getExportFileTitle(
																	notification
																)
															}}</span
														>
													</a>
													ready for download.
												</span>
											</template>
											<template v-else>
												<v-icon
													color="primary"
													size="18"
													class="mr-2"
													>{{
														getNotificationIcon(
															notification.type
														)
													}}</v-icon
												>
												{{ notification.Message }}
											</template>
										</v-list-item-title>
										<v-list-item-subtitle>{{
											moment(
												notification.CreatedAt
											).fromNow()
										}}</v-list-item-subtitle>
									</v-list-item-content>
									<v-list-item-action>
										<v-btn
											fab
											small
											icon
											elevation="1"
											color="error"
											@click.stop="
												onDeleteNotification(
													notification
												)
											"
										>
											<v-icon small>mdi-delete</v-icon>
										</v-btn>
									</v-list-item-action>
								</v-list-item>
							</v-card>
						</div>
					</v-list>
					<div v-else>No notifications</div>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn color="primary" text @click="showDialog = false">
						Close
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import { ProjectContextGetters } from '../../../store/getters';
import { GeneralActions, ProjectContextActions } from '../../../store/actions';
import { notificationTypes } from '../../../enums/notificationType';
import ConfirmDialog from '@/components/core/dialogs/confirmDialog';
import exportDataService from '../../../services/data/dataExportService';

export default {
	name: 'Notifications',
	data() {
		return {
			showDialog: false,
		};
	},
	computed: {
		...mapGetters({
			notifications: [ProjectContextGetters.NOTIFICATIONS],
		}),
	},
	methods: {
		showNotifications() {
			this.showDialog = !this.showDialog;
		},
		moment(date) {
			return moment(date);
		},
		onDeleteNotification(notification) {
			ConfirmDialog({
				message: 'Are you sure you want to delete this notification?',
			}).then((isDelete) => {
				if (isDelete) {
					// Dispatch the action to delete the notification
					this.$store.dispatch(
						ProjectContextActions.DELETE_NOTIFICATION,
						notification.Id
					);
				}
			});
		},
		getNotificationIcon(type) {
			switch (type) {
				case notificationTypes.ExportFileFailed:
				case notificationTypes.ImportFileFailed:
					return 'mdi-alert';
				case notificationTypes.ExportFileSuccess:
					return 'mdi-file-check';
				case 'success':
					return 'mdi-check-circle';
				case 'info':
					return 'mdi-information';
				default:
					return 'mdi-exclamation';
			}
		},
		isExportFile(notification) {
			return notification.type === notificationTypes.ExportFileSuccess;
		},
		getExportFileId(notification) {
			// Extract fileId from format (fileId,Filename)
			if (!notification.Message) return '';
			// Try to extract using regex - pattern to match (fileId,Filename)
			const match = notification.Message.match(/\(([^,]+),(.+?)\)/);
			if (match && match[1]) {
				// match[1] contains just the fileId portion
				return match[1].trim();
			}
			return notification.Message;
		},
		getExportFileTitle(notification) {
			// Extract filename from format (fileId,Filename)
			if (!notification.Message) return '';

			// Try to extract using regex
			const match = notification.Message.match(/\([^,]+,(.+?)\)/);
			if (match && match[1]) {
				return match[1].trim();
			}

			// Fallback to original message if parsing fails
			return notification.Message;
		},

		exportNotificationFile(notification) {
			let fileId = this.getExportFileId(notification);
			let fileName = this.getExportFileTitle(notification);
			this.exportFile(fileId, fileName);
		},

		exportFile(fileId, fileName) {
			this.$store.dispatch(GeneralActions.LOADING, true);
			exportDataService
				.downloadFile(fileId)
				.then((response) => {
					if (response.status === 200) {
						const blob = new Blob([response.data], {
							type: response.headers['content-type'],
						});
						const url = window.URL.createObjectURL(blob);
						const link = document.createElement('a');
						link.href = url;
						link.setAttribute('download', fileName);
						document.body.appendChild(link);
						link.click();
					}
				})
				.finally(() => {
					this.$store.dispatch(GeneralActions.LOADING, false);
				});
		},
	},
};
</script>

<style lang="scss" scoped>
.notifications-menu {
	max-width: 20%;
}

.notifications-list {
	padding: 0 8px;

	.notification-card {
		border-radius: 8px;
	}
}

.export-file-link {
	display: flex;
	align-items: center;
	max-width: 400px;
	overflow: hidden;
	text-decoration: none;
}
.export-file-title {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	display: inline-block;
	vertical-align: middle;
	max-width: 340px;
}

.export-file-row {
	display: flex;
	align-items: center;
	gap: 4px;
	flex-wrap: nowrap;
}
</style>
